import {
  Globe, Plus, Edit, Check, X, ExternalLink,
  DollarSign, Loader2, BookmarkPlus
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function EditListingModal({
  editModal, setEditModal, isManualEntry, setIsManualEntry,
  editingListing, editForm, setEditForm,
  calculateCapRate, handleAutoEstimate, estimating,
  handleSaveEdit, handleSaveManualEntry, savingManual,
  handleApprove
}) {
  return (
    <Dialog open={editModal} onOpenChange={(open) => {
      setEditModal(open);
      if (!open) setIsManualEntry(false);
    }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isManualEntry ? "Add Listing Manually" : "Edit & Complete Listing"}</DialogTitle>
        </DialogHeader>

        {(editingListing || isManualEntry) && (
          <div className="space-y-4 pt-4">
            {editingListing && !isManualEntry && (
              <div className="p-3 bg-gray-50 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Globe className="w-4 h-4" />
                  <span>Source: {editingListing.source_site}</span>
                </div>
                <a href={editingListing.source_url} target="_blank" rel="noopener noreferrer"
                  className="text-[#0066FF] text-xs hover:underline flex items-center gap-1 mt-1">
                  View original listing <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}

            {isManualEntry && (
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <div className="flex items-center gap-2 text-blue-700">
                  <Edit className="w-4 h-4" />
                  <span>Manual Entry - Fill in the listing details below</span>
                </div>
                <p className="text-blue-600 text-xs mt-1">
                  This listing will be created with "pending" status for review before approval.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Title</label>
                <Input value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} placeholder="e.g., Luxury 2BR in Tribeca" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">Address</label>
                <Input value={editForm.address} onChange={(e) => setEditForm({...editForm, address: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Unit Number</label>
                <Input value={editForm.unit_number} onChange={(e) => setEditForm({...editForm, unit_number: e.target.value})} placeholder="e.g., 12A" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Building Name</label>
                <Input value={editForm.building_name} onChange={(e) => setEditForm({...editForm, building_name: e.target.value})} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Neighborhood</label>
                <Input value={editForm.neighborhood} onChange={(e) => setEditForm({...editForm, neighborhood: e.target.value})} placeholder="e.g., Tribeca" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">City</label>
                <Input value={editForm.city} onChange={(e) => setEditForm({...editForm, city: e.target.value})} placeholder="e.g., New York" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Market</label>
                <Select value={editForm.market} onValueChange={(v) => setEditForm({...editForm, market: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nyc">New York City</SelectItem>
                    <SelectItem value="miami">Miami</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Sponsor Name</label>
                <Input value={editForm.sponsor_name} onChange={(e) => setEditForm({...editForm, sponsor_name: e.target.value})} />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Property Details</h4>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price ($)</label>
                  <Input type="number" value={editForm.price} onChange={(e) => setEditForm({...editForm, price: e.target.value})} placeholder="2500000" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bedrooms</label>
                  <Input type="number" value={editForm.bedrooms} onChange={(e) => setEditForm({...editForm, bedrooms: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Bathrooms</label>
                  <Input type="number" step="0.5" value={editForm.bathrooms} onChange={(e) => setEditForm({...editForm, bathrooms: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sqft</label>
                  <Input type="number" value={editForm.sqft} onChange={(e) => setEditForm({...editForm, sqft: e.target.value})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Year Built</label>
                  <Input type="number" value={editForm.year_built} onChange={(e) => setEditForm({...editForm, year_built: e.target.value})} placeholder="2020" />
                </div>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium">Financial Details & Investment Returns</h4>
                <Button type="button" variant="outline" size="sm" onClick={handleAutoEstimate} disabled={estimating || !editForm.sqft} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                  {estimating ? (<><Loader2 className="w-3 h-3 mr-1 animate-spin" />Calculating...</>) : (<><DollarSign className="w-3 h-3 mr-1" />Auto-Estimate All</>)}
                </Button>
              </div>
              <p className="text-xs text-gray-500 mb-3">Estimates based on market ({editForm.market?.toUpperCase() || "NYC"}), square footage, and comparable properties.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">HOA/Common Charges ($/mo)</label>
                  <Input type="number" value={editForm.monthly_common_charges} onChange={(e) => setEditForm({...editForm, monthly_common_charges: e.target.value})} placeholder="Auto-estimate or enter" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Property Taxes ($/mo)</label>
                  <Input type="number" value={editForm.monthly_taxes} onChange={(e) => setEditForm({...editForm, monthly_taxes: e.target.value})} placeholder="Auto-estimate or enter" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-blue-600">Est. Monthly Rent ($) *</label>
                  <Input type="number" value={editForm.projected_monthly_rent} onChange={(e) => setEditForm({...editForm, projected_monthly_rent: e.target.value})} placeholder="Auto-estimate or enter" className="border-blue-200 focus:border-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Est. Cap Rate</label>
                  <div className="h-10 px-3 py-2 bg-gray-100 rounded-md flex items-center">
                    {calculateCapRate() ? (
                      <span className={`font-bold ${parseFloat(calculateCapRate()) >= 4 ? 'text-green-600' : 'text-amber-600'}`}>{calculateCapRate()}%</span>
                    ) : (<span className="text-gray-400 text-sm">Enter rent to calculate</span>)}
                  </div>
                </div>
              </div>
              {calculateCapRate() && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-gray-500">Annual Gross Rent</div>
                      <div className="font-semibold">${((parseFloat(editForm.projected_monthly_rent) || 0) * 12).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Annual Expenses</div>
                      <div className="font-semibold text-red-600">-${(((parseFloat(editForm.monthly_common_charges) || 0) + (parseFloat(editForm.monthly_taxes) || 0)) * 12).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-gray-500">Net Operating Income</div>
                      <div className="font-semibold text-green-600">${(((parseFloat(editForm.projected_monthly_rent) || 0) - (parseFloat(editForm.monthly_common_charges) || 0) - (parseFloat(editForm.monthly_taxes) || 0)) * 12).toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t pt-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} rows={4} />
            </div>

            {!isManualEntry && editingListing?.images?.length > 0 && (
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">Imported Images ({editingListing.images.length})</label>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {editingListing.images.slice(0, 5).map((img, idx) => (
                    <img key={idx} src={img} alt={`Property ${idx + 1}`} className="w-24 h-16 object-cover rounded" />
                  ))}
                  {editingListing.images.length > 5 && (
                    <div className="w-24 h-16 bg-gray-200 rounded flex items-center justify-center text-sm text-gray-500">+{editingListing.images.length - 5} more</div>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => { setEditModal(false); setIsManualEntry(false); }} className="flex-1">Cancel</Button>
              {isManualEntry ? (
                <Button onClick={handleSaveManualEntry} disabled={savingManual} className="flex-1 bg-[#0066FF] hover:bg-[#0052CC]">
                  {savingManual ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating...</>) : (<><Plus className="w-4 h-4 mr-2" />Create Listing</>)}
                </Button>
              ) : (
                <>
                  <Button onClick={handleSaveEdit} className="flex-1">Save Changes</Button>
                  <Button onClick={() => { handleSaveEdit(); handleApprove(editingListing.scraped_id); setEditModal(false); }} className="flex-1 bg-green-600 hover:bg-green-700">Save & Approve</Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SaveSearchModal({
  saveSearchModal, setSaveSearchModal, saveSearchName, setSaveSearchName,
  handleSaveSearch, savingSearch
}) {
  return (
    <Dialog open={saveSearchModal} onOpenChange={setSaveSearchModal}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus className="w-5 h-5 text-amber-600" />
            Save Search
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <label className="block text-sm font-medium mb-1">Search Name</label>
            <Input
              value={saveSearchName}
              onChange={(e) => setSaveSearchName(e.target.value)}
              placeholder="e.g., Manhattan $2-5M Condos"
              data-testid="save-search-name-input"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSaveSearchModal(false)} className="flex-1">Cancel</Button>
            <Button onClick={handleSaveSearch} disabled={savingSearch || !saveSearchName.trim()} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-search-confirm-btn">
              {savingSearch ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>) : (<><Check className="w-4 h-4 mr-2" />Save Search</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BuildingModal({
  buildingModal, closeBuildingModal, editingBuildingId,
  buildingForm, setBuildingForm, creatingBuilding,
  handleUpdateBuilding, handleCreateBuilding
}) {
  return (
    <Dialog open={buildingModal} onOpenChange={(open) => { if (!open) closeBuildingModal(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingBuildingId ? "Edit Building" : "Create New Development Building"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Building Name *</label>
              <Input value={buildingForm.name} onChange={(e) => setBuildingForm({...buildingForm, name: e.target.value})} placeholder="e.g., One Manhattan Square" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Address *</label>
              <Input value={buildingForm.address} onChange={(e) => setBuildingForm({...buildingForm, address: e.target.value})} placeholder="e.g., 252 South Street, New York, NY" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Neighborhood</label>
              <Input value={buildingForm.neighborhood} onChange={(e) => setBuildingForm({...buildingForm, neighborhood: e.target.value})} placeholder="e.g., Two Bridges" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Market</label>
              <Select value={buildingForm.market || "nyc"} onValueChange={(v) => setBuildingForm({...buildingForm, market: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nyc">NYC</SelectItem>
                  <SelectItem value="miami">Miami</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Developer</label>
              <Input value={buildingForm.developer} onChange={(e) => setBuildingForm({...buildingForm, developer: e.target.value})} placeholder="e.g., Macklowe Properties" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Year Built</label>
              <Input type="number" value={buildingForm.year_built} onChange={(e) => setBuildingForm({...buildingForm, year_built: e.target.value})} placeholder="2023" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Total Units</label>
              <Input type="number" value={buildingForm.total_units} onChange={(e) => setBuildingForm({...buildingForm, total_units: e.target.value})} placeholder="566" />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium mb-1">Description</label>
              <Input value={buildingForm.description} onChange={(e) => setBuildingForm({...buildingForm, description: e.target.value})} placeholder="Brief description of the building..." />
            </div>
          </div>

          {!editingBuildingId && (
            <div className="p-3 bg-purple-50 rounded-lg text-sm text-purple-800">
              After creating the building, you can add individual units by pasting their StreetEasy URLs.
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={closeBuildingModal} className="flex-1">Cancel</Button>
            <Button onClick={editingBuildingId ? handleUpdateBuilding : handleCreateBuilding} disabled={creatingBuilding || !buildingForm.name.trim() || !buildingForm.address.trim()} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white">
              {creatingBuilding ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{editingBuildingId ? "Saving..." : "Creating..."}</>) : (<><Check className="w-4 h-4 mr-2" />{editingBuildingId ? "Save Changes" : "Create Building"}</>)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
